
"use client"

import { useState, useEffect, useRef } from "react"
import { Link } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faQuestionCircle,
  faPlus,
  faEye,
  faEdit,
  faTrash,
  faCheck,
  faTimes,
  faSearch,
  faUpload,
  faDownload,
  faExclamationTriangle,
  faSpinner,
  faChevronLeft,
  faChevronRight,
  faEllipsisH,
  faFilter,
  faLanguage,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons"
import { baseUrl } from "../config/baseUrl"
import DOMPurify from "dompurify";
export default function QuestionList() {
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [subjectFilter, setSubjectFilter] = useState("")
  const [topicFilter, setTopicFilter] = useState("")
  const [language, setLanguage] = useState("english")
  const fileInputRef = useRef(null)
  const [questions, setQuestions] = useState([])
  const [subjects, setSubjects] = useState([])
  const [topics, setTopics] = useState([])
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [deleteQuestionId, setDeleteQuestionId] = useState(null)

  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalQuestions, setTotalQuestions] = useState(0)

  const questionsPerPage = 50
  const indexOfLastQuestion = currentPage * questionsPerPage
  const indexOfFirstQuestion = indexOfLastQuestion - questionsPerPage

  useEffect(() => {
    fetchAllData(currentPage)
  }, [])

  const fetchAllData = async (page = 1) => {
    try {
      setLoading(true)

      const params = new URLSearchParams({
        page: page.toString(),
        keyword: searchTerm.trim(),
        status: statusFilter,
        subject: subjectFilter,
        topic: topicFilter,
        language,
      });


      const [questionsResponse, subjectsResponse, topicsResponse] = await Promise.all([
        fetch(`${baseUrl}/question?${params}`, {
          headers: { Authorization: localStorage.getItem("token") || "" },
        }),
        fetch(`${baseUrl}/subject/list`, {
          headers: { Authorization: localStorage.getItem("token") || "" },
        }),
        fetch(`${baseUrl}/topic/list`, {
          headers: { Authorization: localStorage.getItem("token") || "" },
        }),
      ])

      const questionsData = await questionsResponse.json()
      const subjectsData = await subjectsResponse.json()
      const topicsData = await topicsResponse.json()

      if (questionsData.status && Array.isArray(questionsData.data)) {
        setQuestions(questionsData.data)
        setCurrentPage(questionsData.currentPage)
        setTotalPages(questionsData.totalPages)
        setTotalQuestions(questionsData.totalQuestions)
      } else {
        setQuestions([])
        console.error("Error Fetching Question List:", questionsData)
      }

      setSubjects(subjectsData?.data || [])
      setTopics(topicsData?.data || [])
    } catch (error) {
      console.error("Error fetching data:", error)
      console.error("Failed to fetch data")
      setQuestions([])
      setSubjects([])
      setTopics([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setCurrentPage(1)
    fetchAllData(1)
  }, [searchTerm, statusFilter, subjectFilter, topicFilter, language])

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber)
    fetchAllData(pageNumber)
  }

  const getSubjectNameById = (subjectIdOrName) => {
    if (!subjectIdOrName) return "N/A"

    if (typeof subjectIdOrName === "string" && subjectIdOrName.length === 24) {
      const subject = subjects.find((s) => s._id === subjectIdOrName)
      return subject ? subject.name : "Unknown Subject"
    }

    return subjectIdOrName
  }

  const getTopicNameById = (topicIdOrName) => {
    if (!topicIdOrName) return "N/A"

    if (typeof topicIdOrName === "string" && topicIdOrName.length === 24) {
      const topic = topics.find((t) => t._id === topicIdOrName)
      return topic ? topic.name : "Unknown Topic"
    }

    return topicIdOrName
  }

  const getFilteredTopics = () => {
    if (!subjectFilter) return topics;
    return topics.filter((topic) => {
      const topicSubjectId =
        typeof topic.subject === "object" ? topic.subject._id : topic.subject;
      return topicSubjectId === subjectFilter;
    });
  };

  const truncateText = (html, maxLength) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const text = doc.body.textContent || "";

    const truncated = text.length > maxLength ? text.slice(0, maxLength) + "..." : text;

    // Optional: wrap back in <p> to keep formatting consistent
    return `<p>${DOMPurify.sanitize(truncated)}</p>`;
  };

  const getQuestionText = (question, lang) => {
    if (!question) return ""

    switch (lang) {
      case "english":
        return question.question_english
      case "hindi":
        return question.question_hindi
      case "bengali":
        return question.question_bengali
      default:
        return question.question_english
    }
  }

  const getOptionText = (question, lang, option) => {
    if (!question) return ""

    const optionLower = option.toLowerCase()
    const optionUpper = option.toUpperCase()

    switch (lang) {
      case "english":
        return question[`option_${optionLower}_english`] || question[`option_${optionLower}`] || ""
      case "hindi":
        return question[`option_${optionUpper}_hindi`] || question[`option_${optionLower}`] || ""
      case "bengali":
        const optionMap = { a: "1", b: "2", c: "3", d: "4", e: "5" }
        return question[`option_${optionMap[optionLower]}_bengali`] || question[`option_${optionLower}`] || ""
      default:
        return question[`option_${optionLower}_english`] || question[`option_${optionLower}`] || ""
    }
  }



  const getCorrectAnswer = (question) => {
    return question?.correct_answer || question?.correctAnswer || ""
  }


  const currentQuestions = questions

  // const truncateText = (text, maxLength = 50) => {
  //   if (!text) return ""
  //   return text.length > maxLength ? text.substring(0, maxLength) + "..." : text
  // }

  const handleUploadFile = (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    const postData = new FormData()
    postData.append("file", file)

    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }

    apiCall(postData)
  }

  const apiCall = async (file) => {
    try {
      setLoading(true)

      const response = await fetch(`${baseUrl}/question/upload-excel`, {
        method: "POST",
        body: file,
        headers: {
          Authorization: localStorage.getItem("token") || "",
        },
      })

      const data = await response.json()

      if (data.status) {
        await fetchAllData()
        alert(`Successfully uploaded ${data.count || "multiple"} questions`)
      } else {
        console.error(data.message || "Failed to upload questions")
      }
    } catch (error) {
      console.error("Upload error:", error)
      console.error("Failed to upload questions")
    } finally {
      setLoading(false)
    }
  }
  const handleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active"

    try {
      const response = await fetch(`${baseUrl}/question/status/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("token") || "",
        },
        body: JSON.stringify({ status: newStatus }),
      })

      if (!response.ok) {
        throw new Error("Failed to update status")
      }

      setQuestions((prevQuestions) => prevQuestions.map((q) => (q._id === id ? { ...q, status: newStatus } : q)))
      console.log("Status updated successfully")
    } catch (error) {
      console.error("Error updating status:", error)
      alert("Failed to update status")
    }
  }

  const handleDelete = async (id) => {
    setDeleteQuestionId(id)
    setShowConfirmDialog(true)
  }

  const confirmDelete = async () => {
    if (!deleteQuestionId) return

    try {
      const response = await fetch(`${baseUrl}/question/${deleteQuestionId}`, {
        method: "DELETE",
        headers: {
          Authorization: localStorage.getItem("token") || "",
        },
      })

      if (!response.ok) {
        throw new Error("Failed to delete question")
      }

      setQuestions((prevQuestions) => prevQuestions.filter((q) => q._id !== deleteQuestionId))
      alert("Question has been deleted successfully!")
    } catch (error) {
      console.error("Error deleting question:", error)
      alert("Failed to delete question.")
    } finally {
      setShowConfirmDialog(false)
      setDeleteQuestionId(null)
    }
  }

  const cancelDelete = () => {
    setShowConfirmDialog(false)
    setDeleteQuestionId(null)
  }

  return (
    <>
      <style >{`
        .main-sec {
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
          min-height: 100vh;
          padding: 2rem 0;
        }

        .dashboard-title {
          margin-bottom: 1.5rem;
        }

        .dash-head {
          color: #333;
          font-size: 1.8rem;
          font-weight: 700;
          margin: 0;
          display: flex;
          align-items: center;
        }

        .cards {
          background: white;
          border-radius: 15px;
          padding: 2rem;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          margin-bottom: 2rem;
        }

        .bus-filter {
          margin-bottom: 2rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid #eee;
        }

        .card-title {
          color: #333;
          font-weight: 700;
          margin: 0;
        }

        .btn-add-test {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          border-radius: 8px;
          padding: 0.75rem 1.5rem;
          color: white;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          margin-left: 0.5rem;
        }

        .btn-add-test:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
          color: white;
          text-decoration: none;
        }

        .search-filter-container {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
          gap: 1rem;
          margin-bottom: 1.5rem;
          align-items: center;
        }

        .search-container {
          position: relative;
        }

        .search-input {
          width: 100%;
          border-radius: 8px;
          border: 1px solid #ddd;
          padding: 0.75rem 1rem;
          padding-left: 2.5rem;
          font-size: 0.9rem;
        }

        .search-icon {
          position: absolute;
          left: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          color: #999;
        }

        .filter-select {
          border-radius: 8px;
          border: 1px solid #ddd;
          padding: 0.75rem 1rem;
          font-size: 0.9rem;
          width: 100%;
        }

        .custom-table {
          border-radius: 10px;
          overflow: hidden;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }

        .table {
          margin: 0;
          width: 100%;
        }

        .table thead th {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          font-weight: 600;
          border: none;
          padding: 1rem 0.75rem;
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .table tbody td {
          padding: 0.75rem;
          vertical-align: middle;
          border-bottom: 1px solid #f0f0f0;
          font-size: 0.9rem;
        }

        .table tbody tr:hover {
          background-color: #f8f9ff;
        }

        .question-text {
          max-width: 200px;
          font-weight: 500;
          line-height: 1.4;
        }

        .subject-topic-cell {
          min-width: 120px;
        }

        .subject-name {
          display: inline-flex;
          align-items: center;
          padding: 0.25rem 0.75rem;
          background: #e3f2fd;
          color: #1976d2;
          border-radius: 15px;
          font-size: 0.75rem;
          font-weight: 600;
          margin-bottom: 0.25rem;
        }

        .topic-name {
          color: #666;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .correct-answer-cell {
          min-width: 100px;
        }

        .correct-answer-badge {
          display: inline-flex;
          align-items: center;
          padding: 0.25rem 0.75rem;
          background: #e8f5e8;
          color: #2e7d32;
          border-radius: 15px;
          font-size: 0.75rem;
          font-weight: 600;
          margin-bottom: 0.25rem;
        }

        .correct-answer-text {
          color: #666;
          font-size: 0.75rem;
          line-height: 1.3;
        }

        .status-badge {
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
        }

        .status-active {
          background: #d4edda;
          color: #155724;
        }

        .status-inactive {
          background: #f8d7da;
          color: #721c24;
        }

        .status-badge:hover {
          transform: scale(1.05);
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .action-btn {
          width: 32px;
          height: 32px;
          border-radius: 6px;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          font-size: 0.85rem;
        }

        .view-btn {
          background: #4facfe;
          color: white;
        }

        .edit-btn {
          background: #ffa726;
          color: white;
        }

        .delete-btn {
          background: #ef5350;
          color: white;
        }

        .action-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
        }

        .tooltip-text {
          position: absolute;
          bottom: -30px;
          left: 50%;
          transform: translateX(-50%);
          background: #333;
          color: white;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.7rem;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.3s ease;
          white-space: nowrap;
          z-index: 10;
        }

        .action-btn:hover .tooltip-text {
          opacity: 1;
        }

        .empty-state {
          text-align: center;
          padding: 3rem;
        }

        .empty-state img {
          opacity: 0.7;
          margin-bottom: 1rem;
        }

        .empty-state h4 {
          color: #666;
          margin-bottom: 1rem;
        }

        .loading-spinner {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 3rem;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .pagination-container {
          margin-top: 2rem;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .pagination-list {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }

        .pagination-button {
          padding: 0.5rem 1rem;
          border: 1px solid #ddd;
          background: white;
          color: #666;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 0.9rem;
          font-weight: 500;
        }

        .pagination-button:hover:not(:disabled) {
          background: #667eea;
          color: white;
          border-color: #667eea;
        }

        .pagination-button.active {
          background: #667eea;
          color: white;
          border-color: #667eea;
        }

        .pagination-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          max-width: 400px;
          width: 90%;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
        }

        .modal-header {
          display: flex;
          align-items: center;
          margin-bottom: 1rem;
        }

        .modal-icon {
          width: 48px;
          height: 48px;
          background: #fff3cd;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 1rem;
          color: #856404;
          font-size: 1.2rem;
        }

        .modal-title {
          font-size: 1.2rem;
          font-weight: 600;
          color: #333;
          margin: 0;
        }

        .modal-text {
          color: #666;
          margin-bottom: 2rem;
          line-height: 1.5;
        }

        .modal-actions {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
        }

        .modal-btn {
          padding: 0.75rem 1.5rem;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .modal-btn-cancel {
          background: #f8f9fa;
          color: #666;
        }

        .modal-btn-cancel:hover {
          background: #e9ecef;
        }

        .modal-btn-delete {
          background: #dc3545;
          color: white;
        }

        .modal-btn-delete:hover {
          background: #c82333;
        }

        @media (max-width: 1200px) {
          .search-filter-container {
            grid-template-columns: 1fr 1fr 1fr;
            gap: 0.75rem;
          }
        }

        @media (max-width: 768px) {
          .main-sec {
            padding: 1rem 0;
          }

          .cards {
            padding: 1rem;
          }

          .search-filter-container {
            grid-template-columns: 1fr;
            gap: 0.75rem;
          }

          .table-responsive {
            font-size: 0.8rem;
          }

          .question-text {
            max-width: 150px;
          }

          .action-buttons {
            flex-direction: column;
            gap: 0.25rem;
          }

          .action-btn {
            width: 28px;
            height: 28px;
            font-size: 0.75rem;
          }

          .dash-head {
            font-size: 1.4rem;
          }

          .btn-add-test {
            padding: 0.5rem 1rem;
            font-size: 0.85rem;
          }
        }

        @media (max-width: 480px) {
          .table thead th,
          .table tbody td {
            padding: 0.5rem 0.25rem;
          }

          .question-text {
            max-width: 120px;
          }

          .subject-name,
          .correct-answer-badge {
            font-size: 0.7rem;
            padding: 0.2rem 0.5rem;
          }
        }
      `}</style>

      <section className="main-sec">
        <div className="container">
          <div className="row">
            <div className="col-lg-6">
              <div className="dashboard-title">
                <h4 className="dash-head">
                  <FontAwesomeIcon icon={faQuestionCircle} className="me-2" />
                  Questions Management
                </h4>
              </div>
              <div className="custom-bredcump">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/">Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Questions List
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
            <div className="col-lg-6 d-flex justify-content-end align-items-start">
              <div className="d-flex align-items-center">
                <label htmlFor="fileUpload" className="btn-add-test cursor-pointer">
                  <FontAwesomeIcon icon={faUpload} className="me-1" />
                  Bulk Upload
                </label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleUploadFile}
                  id="fileUpload"
                  className="d-none"
                  accept=".xlsx,.xls,.ods,.csv"
                />
                <Link
                  to="/sample_questions.ods"
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-add-test"
                >
                  <FontAwesomeIcon icon={faDownload} className="me-1" />
                  Download Format
                </Link>
                <Link to="create" className="btn-add-test">
                  <FontAwesomeIcon icon={faPlus} className="me-1" />
                  Add Question
                </Link>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-12">
              <div className="cards">
                <div className="bus-filter">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                      <h5 className="card-title">
                        <FontAwesomeIcon icon={faFilter} className="me-2" />
                        Questions List ({totalQuestions} total)
                      </h5>
                    </div>
                    <div>
                      <small className="text-muted">
                        Page {currentPage} of {totalPages}
                      </small>
                    </div>
                  </div>

                  <div className="search-filter-container">
                    <div className="search-container">
                      <FontAwesomeIcon icon={faSearch} className="search-icon" />
                      <input
                        type="text"
                        className="search-input"
                        placeholder="Search questions, subjects, topics..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                    <select
                      className="filter-select"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                    <select
                      className="filter-select"
                      value={subjectFilter}
                      onChange={(e) => {
                        setSubjectFilter(e.target.value);
                        setTopicFilter("");
                      }}
                    >
                      <option value="">All Subjects</option>
                      {subjects.map((subject) => (
                        <option key={subject._id} value={subject._id}>
                          {subject.name}
                        </option>
                      ))}
                    </select>

                    <select
                      className="filter-select"
                      value={topicFilter}
                      onChange={(e) => setTopicFilter(e.target.value)}
                    >
                      <option value="">All Topics</option>
                      {getFilteredTopics().map((topic) => (
                        <option key={topic._id} value={topic._id}>
                          {topic.name}
                        </option>
                      ))}
                    </select>


                    <select className="filter-select" value={language} onChange={(e) => setLanguage(e.target.value)}>
                      <option value="english">
                        <FontAwesomeIcon icon={faLanguage} className="me-1" />
                        English
                      </option>
                      <option value="hindi">हिंदी (Hindi)</option>
                      <option value="bengali">বাংলা (Bengali)</option>
                    </select>
                  </div>
                </div>

                <div className="table-responsive custom-table">
                  <table className="table table-borderless">
                    <thead>
                      <tr>
                        <th style={{ width: "5%" }}>SN</th>
                        <th style={{ width: "25%" }}>Question</th>
                        <th style={{ width: "15%" }}>Subject</th>
                        <th style={{ width: "15%" }}>Topic</th>
                        <th style={{ width: "15%" }}>Correct Answer</th>
                        <th style={{ width: "10%" }}>Status</th>
                        <th style={{ width: "15%" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={7} className="text-center">
                            <div className="loading-spinner">
                              <FontAwesomeIcon icon={faSpinner} className="spinner" />
                            </div>
                          </td>
                        </tr>
                      ) : currentQuestions.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center">
                            <div className="empty-state">
                              <img
                                src="/placeholder.svg?height=200&width=200"
                                alt="No questions"
                                className="mx-auto mb-4"
                                width="200"
                                height="200"
                              />
                              <h4>No Questions found!</h4>
                              <p className="text-muted mb-4">
                                {searchTerm || statusFilter || subjectFilter || topicFilter
                                  ? "Try adjusting your search or filter criteria"
                                  : "Start by adding your first question"}
                              </p>
                              {!searchTerm && !statusFilter && !subjectFilter && !topicFilter && (
                                <Link to="create" className="btn btn-primary">
                                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                                  Add Your First Question
                                </Link>
                              )}
                            </div>
                          </td>
                        </tr>
                      ) : (
                        currentQuestions.map((question, i) => (
                          <tr key={question?._id}>
                            <td>{(currentPage - 1) * questionsPerPage + i + 1}</td>
                            <td>
                              <div
                                key={language}
                                className="question-text"
                                dangerouslySetInnerHTML={{
                                  __html: truncateText(getQuestionText(question, language), 60),
                                }}
                              />


                            </td>
                            <td className="subject-topic-cell">
                              <div className="subject-name">{getSubjectNameById(question?.subject)}</div>
                            </td>
                            <td className="subject-topic-cell">
                              <div className="topic-name">{truncateText(getTopicNameById(question?.topic), 25)}</div>
                            </td>
                            <td className="correct-answer-cell">
                              <div className="correct-answer-badge">
                                <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                Option {getCorrectAnswer(question)}
                              </div>
                              <div className="correct-answer-text">
                                {truncateText(
                                  getOptionText(question, language, getCorrectAnswer(question).toLowerCase()),
                                  20,
                                )}
                              </div>
                            </td>
                            <td>
                              <button
                                onClick={() => handleStatus(question?._id, question?.status)}
                                className={`status-badge ${question.status === "active" ? "status-active" : "status-inactive"
                                  }`}
                                disabled={loading}
                              >
                                <FontAwesomeIcon
                                  icon={question?.status === "active" ? faCheck : faTimes}
                                  className="me-1"
                                />
                                {question?.status === "active" ? "Active" : "Inactive"}
                              </button>
                            </td>
                            <td>
                              <div className="action-buttons">
                                <Link
                                  to={`/questions/view/${question?._id}`}
                                  className="action-btn view-btn"
                                  title="View Question"
                                >
                                  <FontAwesomeIcon icon={faEye} />
                                  <span className="tooltip-text">View</span>
                                </Link>
                                <Link
                                  to={`/questions/edit/${question?._id}`}
                                  className="action-btn edit-btn"
                                  title="Edit Question"
                                >
                                  <FontAwesomeIcon icon={faEdit} />
                                  <span className="tooltip-text">Edit</span>
                                </Link>
                                <button
                                  className="action-btn delete-btn"
                                  onClick={() => handleDelete(question?._id)}
                                  title="Delete Question"
                                  disabled={loading}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                  <span className="tooltip-text">Delete</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div className="pagination-container">
                    <nav aria-label="Questions pagination">
                      <div className="pagination-list">
                        <button
                          className={`pagination-button ${currentPage === 1 ? "disabled" : ""}`}
                          onClick={() => paginate(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          <FontAwesomeIcon icon={faChevronLeft} className="me-1" />
                          Previous
                        </button>

                        {[...Array(totalPages)].map((_, index) => {
                          const pageNumber = index + 1
                          if (
                            pageNumber === 1 ||
                            pageNumber === totalPages ||
                            (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                          ) {
                            return (
                              <button
                                key={pageNumber}
                                className={`pagination-button ${currentPage === pageNumber ? "active" : ""}`}
                                onClick={() => paginate(pageNumber)}
                              >
                                {pageNumber}
                              </button>
                            )
                          } else if (pageNumber === currentPage - 2 || pageNumber === currentPage + 2) {
                            return (
                              <span key={pageNumber} className="pagination-button disabled">
                                <FontAwesomeIcon icon={faEllipsisH} />
                              </span>
                            )
                          }
                          return null
                        })}

                        <button
                          className={`pagination-button ${currentPage === totalPages ? "disabled" : ""}`}
                          onClick={() => paginate(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next
                          <FontAwesomeIcon icon={faChevronRight} className="ms-1" />
                        </button>
                      </div>
                    </nav>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {showConfirmDialog && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <div className="modal-icon">
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </div>
              <h3 className="modal-title">Confirm Delete</h3>
            </div>
            <p className="modal-text">
              Are you sure you want to delete this question? You won't be able to revert this action!
            </p>
            <div className="modal-actions">
              <button onClick={cancelDelete} className="modal-btn modal-btn-cancel">
                Cancel
              </button>
              <button onClick={confirmDelete} className="modal-btn modal-btn-delete">
                <FontAwesomeIcon icon={faTrash} className="me-1" />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
